import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActionIcon, Alert, Badge, Box, Button, Card, Center, Group, Loader, Select,
  Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconRefresh, IconAlertTriangle } from '@tabler/icons-react'
import { formatApiError } from '@/api/errors'
import { useCan } from '@/store/auth'
import {
  getAmbientes, getDivergencias, pedirConciliacao,
  type Ambiente, type AmbientesResposta, type Cobertura, type Divergencia,
  type DivergenciasResposta, type EstadoFaixa, type FaixaRemovida,
} from '@/api/ipam'
import { MapaDeEspaco } from './MapaDeEspaco'
import { calcularFaixa, formatarNumero } from './cidr'

// ============================================================
// Endereçamento IP — a tela que responde "onde estão meus ambientes de verdade".
//
// Porte do protótipo validado em 2026-07-29, com uma diferença deliberada: o
// protótipo tratava "61 rotas lidas" como certeza. Aqui `rotas ===
// 'indeterminadas'` é estado de primeira classe e a tela DIZ que não sabe, em vez
// de mostrar 0 achados numa categoria que não pôde ser calculada (§6.2).
//
// A fonte é a nuvem; o **registro** de endereçamento entra como eixo de intenção
// declarada — nunca como verdade sobre a realidade. Na Fatia 1 o registro é semeado
// da planilha, mas a tela nunca diz "planilha": o vocabulário é o do destino, não o
// da origem, e na Fatia 2 o registro passa a ter dono e data.
//
// Spec: docs/superpowers/specs/2026-07-29-ipam-conciliacao-enderecamento-design.md
// ============================================================

const ROTULO_ESTADO: Record<EstadoFaixa, { texto: string; cor: string }> = {
  vivo_roteado:       { texto: 'em uso · roteada',      cor: 'teal' },
  vivo:              { texto: 'em uso, sem rota',      cor: 'blue' },
  rota_indeterminada: { texto: 'rota não verificada',   cor: 'gray' },
  publico:           { texto: 'espaço público',        cor: 'red' },
  vpc_default:       { texto: 'default da AWS',        cor: 'gray' },
}

const ROTULO_DIVERGENCIA: Record<string, string> = {
  sobreposicao:          'faixas sobrepostas',
  nao_rfc1918:           'espaço público em uso interno',
  vpc_default:           'VPC default da AWS',
  sem_rota_no_hub:       'sem rota no hub',
  vpc_sem_registro:      'existe na AWS, fora do registro',
  registro_sem_vpc:      'reservada sem VPC na faixa',
  existe_em_outro_bloco: 'existe em outro bloco',
  fora_da_organizacao:   'fora desta organização',
  fora_do_padrao:        'fora da convenção de slot',
}

/**
 * `maxWidth: none` + `overflow: visible` no label: o Badge do Mantine trunca com
 * reticências por padrão, e numa tabela de 13 colunas isso transformava a coluna
 * mais importante em "em u…" / "espa…" — sem distinguir roteada de não-roteada.
 * Com o truncamento desligado, a coluna estica em vez de mentir.
 */
const SEM_TRUNCAR = {
  root: { maxWidth: 'none' as const },
  label: { overflow: 'visible' as const, textOverflow: 'clip' as const },
}

function EstadoBadge({ estado }: { estado: EstadoFaixa }) {
  const r = ROTULO_ESTADO[estado] ?? { texto: estado, cor: 'gray' }
  return (
    <Badge size="xs" variant="light" color={r.cor} styles={SEM_TRUNCAR}
           style={{ textTransform: 'none' }}>
      {r.texto}
    </Badge>
  )
}

/** Painel de cobertura. Silêncio não pode parecer sucesso (§6.1). */
function PainelCobertura({ cobertura, rotas }: { cobertura: Cobertura; rotas: string }) {
  if (cobertura.sem_execucao) {
    return (
      <Alert color="gray" icon={<IconAlertTriangle size={16} />} title="A conciliação nunca rodou">
        Nenhuma coleta foi concluída ainda. Esta tela está vazia porque não há dado —
        não porque não haja divergência.
      </Alert>
    )
  }

  const falhas = Object.entries(cobertura.per_account ?? {})
    .filter(([, v]) => v?.status && v.status !== 'ok')
  const parcial = cobertura.status !== 'success'

  return (
    <Alert
      color={parcial ? 'yellow' : 'teal'}
      icon={parcial ? <IconAlertTriangle size={16} /> : undefined}
      title={`${cobertura.accounts_ok ?? 0} de ${cobertura.accounts_total ?? 0} contas coletadas`}
    >
      <Stack gap={4}>
        <Text size="sm">
          Regiões varridas: <b>{(cobertura.regions ?? []).join(' · ') || '—'}</b>. O que
          estiver fora delas não existe para esta tela.
          {(cobertura.accounts_failed ?? 0) > 0 && (
            <> <b>{cobertura.accounts_failed} conta(s) não verificada(s)</b> — o que estiver
            só nelas não aparece aqui.</>
          )}
        </Text>
        {rotas === 'indeterminadas' ? (
          <Text size="sm">
            As rotas do hub <b>não puderam ser lidas</b> nesta execução, então
            “sem rota no hub” e “fora desta organização” estão <b>indeterminadas</b> —
            não zeradas. Falta <code>ec2:DescribeTransitGatewayRouteTables</code> na
            role de execução.
          </Text>
        ) : (
          <Text size="sm">
            <b>{cobertura.routes_read ?? 0} rotas</b> do hub lidas e cruzadas com as faixas.
          </Text>
        )}
        {falhas.length > 0 && (
          <Text size="xs" c="dimmed" ff="monospace">
            não coletadas: {falhas.map(([conta, v]) => `${conta} (${v.status})`).join(', ')}
          </Text>
        )}
        {cobertura.finished_at && (
          <Text size="xs" c="dimmed" ff="monospace">coleta concluída em {cobertura.finished_at}</Text>
        )}
      </Stack>
    </Alert>
  )
}

/**
 * `n: null` renderiza **—**, nunca `0`.
 *
 * Um `0` em corpo 27 com "indeterminado" em corpo 12 embaixo é o §6.2 na forma
 * visual: o olho lê o zero e conclui "nada a fazer aqui". Número só aparece
 * quando existe número.
 */
function Tile(
  { n, rotulo, descricao, cor }:
  { n: number | null; rotulo: string; descricao: string; cor: string },
) {
  return (
    <Card withBorder padding="sm" style={{ borderLeft: `3px solid var(--mantine-color-${cor}-6)` }}>
      <Text ff="monospace" fw={600} size="27px" lh={1.1} c={n === null ? 'dimmed' : undefined}>
        {n === null ? '—' : formatarNumero(n)}
      </Text>
      <Text ff="monospace" size="10.5px" c="dimmed" tt="uppercase" mt={3}>{rotulo}</Text>
      <Text size="xs" mt={6}>{descricao}</Text>
    </Card>
  )
}

export function EnderecamentoPage() {
  const can = useCan()
  const podeConciliar = can('ipam', 'manage')
  const [amb, setAmb] = useState<AmbientesResposta | null>(null)
  const [div, setDiv] = useState<DivergenciasResposta | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [pedindo, setPedindo] = useState(false)

  const [busca, setBusca] = useState('')
  const [fEstado, setFEstado] = useState<string | null>(null)
  const [fRegiao, setFRegiao] = useState<string | null>(null)
  const [fRegistro, setFRegistro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const [a, d] = await Promise.all([getAmbientes(), getDivergencias()])
      setAmb(a)
      setDiv(d)
    } catch (e) {
      setErro(formatApiError(e))
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => { void carregar() }, [carregar])

  const solicitar = async (accountId?: string) => {
    setPedindo(true)
    try {
      const r = await pedirConciliacao(accountId)
      notifications.show({ color: 'teal', title: 'Conciliação pedida', message: r.mensagem })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Não deu para pedir', message: formatApiError(e) })
    } finally {
      setPedindo(false)
    }
  }

  const itens = div?.itens ?? []
  const indeterminadas = div?.indeterminadas ?? []
  const rotas = amb?.rotas ?? div?.rotas ?? 'indeterminadas'
  // Sem execução, nenhum número desta tela é afirmação — é ausência de dado.
  const semDado = amb?.cobertura?.sem_execucao ?? true
  const conta = (n: number) => (semDado ? null : n)

  // As faixas reais — sem as default da AWS, que são 140 de 223 e afogariam tudo.
  const reais = useMemo(() => (amb?.ambientes ?? []).filter((a) => !a.default), [amb])
  const defaults = (amb?.ambientes ?? []).length - reais.length

  // O mapa é sobre ocupação de slot /20 dentro de um /16. Faixa de espaço público
  // é um /16 inteiro (são 22): entra pintando a linha completa, some 18 linhas ao
  // mapa e não responde "onde há /20 livre" — a pergunta que o mapa existe para
  // responder. Elas seguem na sua própria tabela, com as 22 linhas.
  const mapeaveis = useMemo(() => reais.filter((a) => a.estado !== 'publico'), [reais])
  const blocosPublicos = useMemo(
    () => new Set(reais.filter((a) => a.estado === 'publico')
      .map((a) => calcularFaixa(a.cidr).bloco16)).size,
    [reais],
  )

  const porTipo = useCallback(
    (tipo: string) => itens.filter((i) => i.tipo === tipo),
    [itens],
  )

  const sobreposicoes = porTipo('sobreposicao')
  const publicos = porTipo('nao_rfc1918')
  const reservadas = useMemo(
    () => itens.filter((i) => ['registro_sem_vpc', 'existe_em_outro_bloco', 'fora_da_organizacao'].includes(i.tipo)),
    [itens],
  )
  const semRota = porTipo('sem_rota_no_hub')
  const semRegistro = porTipo('vpc_sem_registro')

  const regioes = useMemo(
    () => [...new Set(reais.map((a) => a.regiao))].sort(),
    [reais],
  )

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase()
    return reais.filter((a) => {
      if (fEstado && a.estado !== fEstado) return false
      if (fRegiao && a.regiao !== fRegiao) return false
      if (fRegistro === 'sim' && !a.no_registro) return false
      if (fRegistro === 'nao' && a.no_registro) return false
      if (!q) return true
      return [a.projeto, a.ambiente, a.cidr, a.conta, a.conta_nome, a.vpc, a.vpc_nome]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    })
  }, [reais, busca, fEstado, fRegiao, fRegistro])

  if (carregando) {
    return (
      <>
        <Center h={280}><Loader /></Center>
      </>
    )
  }

  return (
    <>
      {/* Titulo, trilha e descricao vem do layout desde 2026-08-13 (routeHeader). O que o
          layout nao tem como saber e a acao de cada pagina, e criar contexto ou portal para
          duas telas seria maquinaria demais — a acao fica no topo do conteudo. */}
      {podeConciliar && (
        <Group justify="flex-end" px="md" pt="md">
          <Button
            size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={pedindo} onClick={() => void solicitar()}
          >
            Conciliar agora
          </Button>
        </Group>
      )}

      <Stack gap="lg" p="md">
        <Text size="sm" c="dimmed" maw="72ch">
          O que existe de fato nas contas AWS, conciliado contra o registro de
          endereçamento. A fonte é a nuvem; o registro entra para marcar o que foi
          reservado e nunca construído.
        </Text>

        {erro && <Alert color="red" title="Não deu para carregar">{erro}</Alert>}

        {amb && <PainelCobertura cobertura={amb.cobertura} rotas={rotas} />}

        {amb && amb.removidas.length > 0 && (
          <PainelRemovidas removidas={amb.removidas} />
        )}

        {indeterminadas.length > 0 && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Categorias indeterminadas">
            {indeterminadas.map((t) => ROTULO_DIVERGENCIA[t] ?? t).join(' e ')} não puderam ser
            calculadas nesta execução. Elas aparecem vazias por falta de sinal, não por ausência
            de divergência.
          </Alert>
        )}

        <Box
          style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))',
          }}
        >
          <Tile n={conta(reais.length)} rotulo="faixas em uso" cor="teal"
                descricao={semDado
                  ? 'nenhuma coleta concluída ainda'
                  : `VPCs existentes; ${formatarNumero(defaults)} default da AWS ficam fora das contagens`} />
          <Tile n={semDado || rotas === 'indeterminadas'
                  ? null
                  : reais.filter((a) => a.estado === 'vivo_roteado').length}
                rotulo="alcançáveis pelo hub" cor="blue"
                descricao={rotas === 'indeterminadas'
                  ? 'indeterminado — as rotas do hub não foram lidas'
                  : `as demais ${formatarNumero(reais.length - reais.filter((a) => a.estado === 'vivo_roteado').length)} existem e não são roteadas`} />
          <Tile n={conta(reservadas.length)} rotulo="alocadas sem VPC" cor="yellow"
                descricao="constam no registro e não têm VPC nessa faixa" />
          <Tile n={conta(porTipo('existe_em_outro_bloco').length)} rotulo="existem em outro bloco" cor="violet"
                descricao="alocadas num bloco e provisionadas em outro" />
          <Tile n={conta(sobreposicoes.length)} rotulo="faixas sobrepostas" cor="red"
                descricao="pares de VPCs disputando o mesmo espaço" />
          <Tile n={conta(publicos.length)} rotulo="em espaço público" cor="red"
                descricao="faixas fora da RFC 1918 usadas como privadas" />
          <Tile n={conta(semRegistro.length)} rotulo="fora do registro" cor="violet"
                descricao="existem na AWS e ninguém registrou" />
        </Box>

        {/* ── mapa do espaço ── */}
        <Card withBorder padding="md">
          <Title order={4} ff="monospace" tt="uppercase" fz={13} mb={4}>Mapa do espaço</Title>
          <Text size="sm" c="dimmed" mb="sm" maw="74ch">
            Cada linha é um bloco <code>/16</code>; cada célula é um dos 16 slots{' '}
            <code>/20</code> dentro dele, identificada pelo terceiro octeto. É a visão que a
            registro não dá: onde há espaço livre de verdade e onde duas VPCs disputam a mesma
            faixa. Passe o cursor numa célula para ver quem está nela.
            {blocosPublicos > 0 && (
              <> Os <b>{blocosPublicos} blocos de espaço público</b> ficam fora do mapa — cada um
              é um <code>/16</code> inteiro, encheria a linha toda e não diz nada sobre slot{' '}
              <code>/20</code> livre. Eles estão listados em “Espaço público em uso interno”.</>
            )}
          </Text>
          <MapaDeEspaco ambientes={mapeaveis} divergencias={itens} />
        </Card>

        {/* ── ambientes em uso ── */}
        <Card withBorder padding="md">
          <Group justify="space-between" align="baseline" mb={4}>
            <Title order={4} ff="monospace" tt="uppercase" fz={13}>Ambientes em uso</Title>
            <Text size="xs" c="dimmed" ff="monospace">
              {visiveis.length} de {reais.length} faixas
            </Text>
          </Group>
          <Text size="sm" c="dimmed" mb="sm" maw="74ch">
            Uma linha por faixa de VPC existente — máscara, broadcast, contagem e faixa
            útil são calculadas do CIDR, não digitadas.
          </Text>

          <Group gap="xs" mb="sm">
            <TextInput
              size="xs" w={240} placeholder="filtrar projeto, conta, VPC, CIDR…"
              aria-label="Filtrar ambientes"
              value={busca} onChange={(e) => setBusca(e.currentTarget.value)}
            />
            <Select
              size="xs" w={180} clearable placeholder="todos os estados" aria-label="Filtrar por estado"
              value={fEstado} onChange={setFEstado}
              data={[
                { value: 'vivo_roteado', label: 'em uso e roteada' },
                { value: 'vivo', label: 'em uso, sem rota' },
                { value: 'rota_indeterminada', label: 'rota não verificada' },
                { value: 'publico', label: 'espaço público' },
              ]}
            />
            <Select
              size="xs" w={150} clearable placeholder="todas as regiões" aria-label="Filtrar por região"
              value={fRegiao} onChange={setFRegiao}
              data={regioes.map((r) => ({ value: r, label: r }))}
            />
            <Select
              size="xs" w={200} clearable placeholder="registro: indiferente"
              aria-label="Filtrar por presença no registro"
              value={fRegistro} onChange={setFRegistro}
              data={[
                { value: 'sim', label: 'consta no registro' },
                { value: 'nao', label: 'fora do registro' },
              ]}
            />
          </Group>

          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover fz="xs" style={{ whiteSpace: 'nowrap' }}>
              <Table.Thead>
                <Table.Tr>
                  {/* PRIMEIRA coluna de propósito. Esta tabela tem 13 colunas
                      monoespaçadas com `nowrap` dentro de um contêiner de rolagem
                      horizontal — no extremo direito a ação ficava fora da tela e
                      ninguém a encontrava. */}
                  {podeConciliar && <Table.Th w={34} aria-label="Ações" />}
                  <Table.Th>Projeto</Table.Th>
                  <Table.Th>Ambiente</Table.Th>
                  <Table.Th>Estado</Table.Th>
                  <Table.Th>Netmask</Table.Th>
                  <Table.Th>CIDR base</Table.Th>
                  <Table.Th>Broadcast</Table.Th>
                  <Table.Th ta="right">Endereços</Table.Th>
                  <Table.Th>Primeiro IP</Table.Th>
                  <Table.Th>Último IP</Table.Th>
                  <Table.Th>Conta</Table.Th>
                  <Table.Th>Região</Table.Th>
                  <Table.Th>VPC</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visiveis.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={podeConciliar ? 13 : 12}>
                      <Text size="sm" c="dimmed" py="md">
                        {semDado ? SEM_DADO : 'Nenhuma faixa corresponde a esse filtro.'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : visiveis.map((a) => (
                  <LinhaAmbiente
                    key={`${a.conta}-${a.vpc}-${a.cidr}`} a={a}
                    pedindo={pedindo}
                    onAtualizar={podeConciliar
                      ? () => void solicitar(a.conta) : undefined}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>

        {/* ── reservadas sem VPC na faixa ── */}
        <Card withBorder padding="md">
          <Group justify="space-between" align="baseline" mb={4}>
            <Title order={4} ff="monospace" tt="uppercase" fz={13}>
              Faixas alocadas sem VPC na faixa
            </Title>
            <Text size="xs" c="dimmed" ff="monospace">{reservadas.length} faixas</Text>
          </Group>
          <Text size="sm" c="dimmed" mb="sm" maw="82ch">
            Faixas que constam no registro e para as quais não há VPC <i>nessa faixa</i>. Não
            confundir com “ambiente inexistente”: para algumas delas o ambiente existe, em outro
            bloco — a última coluna diz onde. O casamento é feito pelo nome da VPC e é
            conservador de propósito: uma faixa marcada só como “sem VPC na faixa” pode ter
            ambiente vivo sob um nome que o sistema não reconheceu.
          </Text>
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover fz="xs" style={{ whiteSpace: 'nowrap' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Projeto</Table.Th>
                  <Table.Th>Ambiente</Table.Th>
                  <Table.Th>Situação</Table.Th>
                  <Table.Th>Netmask</Table.Th>
                  <Table.Th>CIDR base</Table.Th>
                  <Table.Th>Broadcast</Table.Th>
                  <Table.Th ta="right">Endereços</Table.Th>
                  <Table.Th>Onde o ambiente existe de fato</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {reservadas.length === 0 ? (
                  <VazioLinha
                    colunas={8}
                    semDado={semDado}
                    texto={indeterminadas.includes('fora_da_organizacao')
                      ? 'Nada a mostrar com as rotas do hub indeterminadas.'
                      : 'Nenhuma faixa declarada está sem VPC.'}
                  />
                ) : reservadas.map((d, i) => <LinhaReservada key={`${d.tipo}-${d.cidr}-${i}`} d={d} />)}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>

        {/* ── existem na AWS e ninguém registrou ── */}
        <Card withBorder padding="md">
          <Group justify="space-between" align="baseline" mb={4}>
            <Title order={4} ff="monospace" tt="uppercase" fz={13}>
              Existem na AWS e ninguém registrou
            </Title>
            <Text size="xs" c="dimmed" ff="monospace">{semRegistro.length} faixas</Text>
          </Group>
          <Text size="sm" c="dimmed" mb="sm" maw="82ch">
            VPCs que existem de fato e que nenhuma linha do registro cobre. É o lado do
            problema que a conferência manual nunca alcança: ler o registro de ponta a ponta
            não revela o que está fora dele. Registrar cada uma, ou aposentar a VPC, é o que
            faz este número cair.
          </Text>
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover fz="xs" style={{ whiteSpace: 'nowrap' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>CIDR</Table.Th>
                  <Table.Th ta="right">Endereços</Table.Th>
                  <Table.Th>Conta</Table.Th>
                  <Table.Th>Região</Table.Th>
                  <Table.Th>VPC</Table.Th>
                  <Table.Th>Nome da VPC</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {semRegistro.length === 0
                  ? <VazioLinha colunas={6} semDado={semDado}
                                texto="Toda faixa real está coberta pelo registro." />
                  : semRegistro.map((d, i) => (
                    <Table.Tr key={`${d.cidr}-${i}`}>
                      <Table.Td ff="monospace" fw={600}>{d.cidr}</Table.Td>
                      <Table.Td ff="monospace" ta="right">{formatarNumero(calcularFaixa(d.cidr).count)}</Table.Td>
                      <Table.Td ff="monospace">{d.detalhe.conta ?? '—'}</Table.Td>
                      <Table.Td ff="monospace" c="dimmed">{d.detalhe.regiao ?? '—'}</Table.Td>
                      <Table.Td ff="monospace" c="dimmed">{d.detalhe.vpc ?? '—'}</Table.Td>
                      <Table.Td>{d.detalhe.vpc_nome || '—'}</Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>

        {/* ── sobreposições ── */}
        <Card withBorder padding="md">
          <Group justify="space-between" align="baseline" mb={4}>
            <Title order={4} ff="monospace" tt="uppercase" fz={13}>Faixas sobrepostas</Title>
            <Text size="xs" c="dimmed" ff="monospace">{sobreposicoes.length} pares</Text>
          </Group>
          <Text size="sm" c="dimmed" mb="sm" maw="82ch">
            Pares de VPCs existentes cujas faixas se cruzam. Sobreposição dentro da mesma conta
            não tem justificativa de roteamento; entre contas distintas pode ser intencional
            quando um bloco maior engloba os menores — a primeira coluna diz qual é o caso.
          </Text>
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover fz="xs" style={{ whiteSpace: 'nowrap' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Tipo</Table.Th>
                  <Table.Th>Faixa A</Table.Th>
                  <Table.Th>Conta A</Table.Th>
                  <Table.Th>VPC A</Table.Th>
                  <Table.Th>Faixa B</Table.Th>
                  <Table.Th>Conta B</Table.Th>
                  <Table.Th>VPC B</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sobreposicoes.length === 0
                  ? <VazioLinha colunas={7} semDado={semDado} texto="Nenhuma faixa real se cruza." />
                  : sobreposicoes.map((d, i) => <LinhaSobreposicao key={`${d.cidr}-${i}`} d={d} />)}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>

        {/* ── espaço público ── */}
        <Card withBorder padding="md">
          <Group justify="space-between" align="baseline" mb={4}>
            <Title order={4} ff="monospace" tt="uppercase" fz={13}>
              Espaço público em uso interno
            </Title>
            <Text size="xs" c="dimmed" ff="monospace">{publicos.length} faixas</Text>
          </Group>
          <Text size="sm" c="dimmed" mb="sm" maw="82ch">
            Faixas que não são RFC 1918. São endereços roteáveis na internet, de posse de
            terceiros, usados como se fossem privados — quem estiver nessas faixas não alcança
            os hosts reais desses blocos.
          </Text>
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover fz="xs" style={{ whiteSpace: 'nowrap' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>CIDR</Table.Th>
                  <Table.Th ta="right">Endereços</Table.Th>
                  <Table.Th>Conta</Table.Th>
                  <Table.Th>Região</Table.Th>
                  <Table.Th>VPC</Table.Th>
                  <Table.Th>Nome da VPC</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {publicos.length === 0
                  ? <VazioLinha colunas={6} semDado={semDado} texto="Nenhuma faixa fora da RFC 1918." />
                  : publicos.map((d, i) => (
                    <Table.Tr key={`${d.cidr}-${i}`}>
                      <Table.Td ff="monospace" fw={600}>{d.cidr}</Table.Td>
                      <Table.Td ff="monospace" ta="right">{formatarNumero(calcularFaixa(d.cidr).count)}</Table.Td>
                      <Table.Td ff="monospace">{d.detalhe.conta ?? '—'}</Table.Td>
                      <Table.Td ff="monospace" c="dimmed">{d.detalhe.regiao ?? '—'}</Table.Td>
                      <Table.Td ff="monospace" c="dimmed">{d.detalhe.vpc ?? '—'}</Table.Td>
                      <Table.Td>{d.detalhe.vpc_nome || '—'}</Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>

        {/* ── sem rota no hub ── */}
        <Card withBorder padding="md">
          <Group justify="space-between" align="baseline" mb={4}>
            <Title order={4} ff="monospace" tt="uppercase" fz={13}>Faixas que o hub não alcança</Title>
            <Text size="xs" c="dimmed" ff="monospace">
              {indeterminadas.includes('sem_rota_no_hub') ? 'indeterminado' : `${semRota.length} faixas`}
            </Text>
          </Group>
          {indeterminadas.includes('sem_rota_no_hub') ? (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              A leitura das rotas do TGW falhou nesta execução, então esta categoria está
              <b> indeterminada</b> — não vazia. Mostrar “0 faixas” aqui seria afirmar que
              tudo está roteado sem ter verificado nada.
            </Alert>
          ) : (
            <Box style={{ overflowX: 'auto' }}>
              <Table striped highlightOnHover fz="xs" style={{ whiteSpace: 'nowrap' }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>CIDR</Table.Th>
                    <Table.Th>Conta</Table.Th>
                    <Table.Th>Região</Table.Th>
                    <Table.Th>VPC</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {semRota.length === 0
                    ? <VazioLinha colunas={4} semDado={semDado} texto="Todas as faixas reais são alcançáveis pelo hub." />
                    : semRota.map((d, i) => (
                      <Table.Tr key={`${d.cidr}-${i}`}>
                        <Table.Td ff="monospace" fw={600}>{d.cidr}</Table.Td>
                        <Table.Td ff="monospace">{d.detalhe.conta ?? '—'}</Table.Td>
                        <Table.Td ff="monospace" c="dimmed">{d.detalhe.regiao ?? '—'}</Table.Td>
                        <Table.Td ff="monospace" c="dimmed">{d.detalhe.vpc ?? '—'}</Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}
        </Card>

        <Text size="xs" c="dimmed" ff="monospace">
          Leitura apenas: <code>ec2:DescribeVpcs</code> por conta e as rotas do TGW hub.
          Nenhuma alteração é feita em nenhuma conta.
        </Text>
      </Stack>
    </>
  )
}

/**
 * Texto de tabela vazia. `semDado` NUNCA usa o texto tranquilizador.
 *
 * "Todas as faixas reais são alcançáveis pelo hub" sobre um banco sem nenhuma
 * coleta é a mentira mais confortável que esta tela poderia contar — o §6.2 na
 * forma de estado vazio. Sem dado, a tela diz que não tem dado.
 */
const SEM_DADO = 'Sem dado: a conciliação ainda não rodou.'

function VazioLinha(
  { colunas, texto, semDado }: { colunas: number; texto: string; semDado?: boolean },
) {
  return (
    <Table.Tr>
      <Table.Td colSpan={colunas}>
        <Text size="sm" c="dimmed" py="md">{semDado ? SEM_DADO : texto}</Text>
      </Table.Td>
    </Table.Tr>
  )
}

function LinhaAmbiente({ a, pedindo, onAtualizar }: {
  a: Ambiente
  pedindo: boolean
  /** Ausente quando o usuário não tem `ipam:manage`. */
  onAtualizar?: () => void
}) {
  const f = calcularFaixa(a.cidr)
  return (
    <Table.Tr>
      {onAtualizar && (
        <Table.Td>
          <Tooltip label={`Reconciliar a conta ${a.conta_nome || a.conta}`} withArrow>
            <ActionIcon size="sm" variant="subtle" loading={pedindo}
                        aria-label="Reconciliar esta conta"
                        onClick={onAtualizar}>
              <IconRefresh size={14} />
            </ActionIcon>
          </Tooltip>
        </Table.Td>
      )}
      <Table.Td>{a.projeto ?? <Text span c="dimmed">—</Text>}</Table.Td>
      <Table.Td>{a.ambiente ?? <Text span c="dimmed">—</Text>}</Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <EstadoBadge estado={a.estado} />
          {/* A faixa continua contando: ninguém confirmou que sumiu. O rótulo
              existe para a tela não apresentar dado velho como se fosse fresco. */}
          {a.vigencia === 'desatualizada' && (
            <Tooltip label={`Último dado confirmado em ${a.desde}`} withArrow>
              <Badge size="xs" variant="light" color="gray">desatualizada</Badge>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td ff="monospace" c="dimmed">{f.netmask}</Table.Td>
      <Table.Td ff="monospace" fw={600}>{a.cidr}</Table.Td>
      <Table.Td ff="monospace" c="dimmed">{f.broadcast}</Table.Td>
      <Table.Td ff="monospace" ta="right">{formatarNumero(f.count)}</Table.Td>
      <Table.Td ff="monospace" c="dimmed">{f.first}</Table.Td>
      <Table.Td ff="monospace" c="dimmed">{f.last}</Table.Td>
      <Table.Td ff="monospace">
        <Tooltip label={a.conta_nome || a.conta} withArrow>
          <Text span size="xs" ff="monospace">{a.conta}</Text>
        </Tooltip>
      </Table.Td>
      <Table.Td ff="monospace" c="dimmed">{a.regiao}</Table.Td>
      <Table.Td ff="monospace" c="dimmed">
        {a.vpc}{a.vpc_nome ? ` ${a.vpc_nome}` : ''}
      </Table.Td>
    </Table.Tr>
  )
}

/**
 * Faixas que sumiram da AWS.
 *
 * Ficam fora das contagens e do mapa de espaço — mas aparecem, porque o histórico
 * tem valor e porque a ausência silenciosa foi justamente o defeito: antes disto,
 * VPC excluída no console continuava listada como se existisse.
 */
function PainelRemovidas({ removidas }: { removidas: FaixaRemovida[] }) {
  return (
    <Card withBorder padding="sm">
      <Text fw={600} size="sm">
        Faixas que sumiram da AWS ({formatarNumero(removidas.length)})
      </Text>
      <Text size="xs" c="dimmed" mt={4} maw="72ch">
        Uma varredura leu a conta com sucesso e não encontrou estas faixas. Ficam
        fora das contagens e do mapa de espaço; continuam aqui como histórico.
      </Text>
      <Table.ScrollContainer minWidth={640} mt="sm">
        <Table fz="xs" withTableBorder striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>CIDR</Table.Th>
              <Table.Th>Conta</Table.Th>
              <Table.Th>Região</Table.Th>
              <Table.Th>VPC</Table.Th>
              <Table.Th>Ausência confirmada em</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {removidas.map((r) => (
              <Table.Tr key={`${r.conta}-${r.regiao}-${r.vpc}-${r.cidr}`}>
                <Table.Td ff="monospace" fw={600}>{r.cidr}</Table.Td>
                <Table.Td ff="monospace">
                  <Tooltip label={r.conta_nome || r.conta} withArrow>
                    <Text span size="xs" ff="monospace">{r.conta}</Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td ff="monospace" c="dimmed">{r.regiao}</Table.Td>
                <Table.Td ff="monospace" c="dimmed">
                  {r.vpc}{r.vpc_nome ? ` ${r.vpc_nome}` : ''}
                </Table.Td>
                <Table.Td ff="monospace" c="dimmed">{r.desde}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  )
}

function LinhaReservada({ d }: { d: Divergencia }) {
  const f = calcularFaixa(d.cidr)
  const cor = d.tipo === 'existe_em_outro_bloco' ? 'violet'
    : d.tipo === 'fora_da_organizacao' ? 'violet' : 'yellow'
  const situacao = d.tipo === 'existe_em_outro_bloco' ? 'existe em outro bloco'
    : d.tipo === 'fora_da_organizacao' ? 'fora da organização'
    : d.detalhe.rota === 'indeterminada' ? 'sem VPC · rota não verificada'
    : 'sem VPC na faixa'
  const onde = d.detalhe.onde ?? []

  return (
    <Table.Tr>
      <Table.Td>{d.detalhe.projeto ?? <Text span c="dimmed">—</Text>}</Table.Td>
      <Table.Td>{d.detalhe.ambiente ?? <Text span c="dimmed">—</Text>}</Table.Td>
      <Table.Td>
        <Badge size="xs" variant="light" color={cor} style={{ textTransform: 'none' }}>{situacao}</Badge>
      </Table.Td>
      <Table.Td ff="monospace" c="dimmed">{f.netmask}</Table.Td>
      <Table.Td ff="monospace" fw={600}>{d.cidr}</Table.Td>
      <Table.Td ff="monospace" c="dimmed">{f.broadcast}</Table.Td>
      <Table.Td ff="monospace" ta="right">{formatarNumero(f.count)}</Table.Td>
      <Table.Td style={{ whiteSpace: 'normal' }}>
        {onde.length === 0 ? <Text span c="dimmed">—</Text> : (
          <Stack gap={2}>
            {onde.map((o, i) => (
              <Text key={i} size="xs">
                <Text span ff="monospace" fw={600}>{o.cidr}</Text>{' '}
                <Text span c="dimmed">{o.vpc_nome || o.vpc} · conta {o.conta}</Text>
              </Text>
            ))}
            {d.detalhe.confianca === 'heuristica_por_nome' && (
              <Text size="10px" c="dimmed">casamento por nome da VPC — sugestão a confirmar</Text>
            )}
          </Stack>
        )}
      </Table.Td>
    </Table.Tr>
  )
}

function LinhaSobreposicao({ d }: { d: Divergencia }) {
  const { a, b, mesma_conta: mesmaConta, identico } = d.detalhe
  const tipo = mesmaConta ? 'mesma conta' : identico ? 'faixa idêntica' : 'contida'
  const cor = mesmaConta || identico ? 'red' : 'yellow'
  return (
    <Table.Tr>
      <Table.Td>
        <Badge size="xs" variant="light" color={cor} style={{ textTransform: 'none' }}>{tipo}</Badge>
      </Table.Td>
      <Table.Td ff="monospace" fw={600}>{a?.cidr ?? d.cidr}</Table.Td>
      <Table.Td ff="monospace">{a?.conta ?? '—'}</Table.Td>
      <Table.Td ff="monospace" c="dimmed">{[a?.vpc, a?.vpc_nome].filter(Boolean).join(' ') || '—'}</Table.Td>
      <Table.Td ff="monospace" fw={600}>{b?.cidr ?? '—'}</Table.Td>
      <Table.Td ff="monospace">{b?.conta ?? '—'}</Table.Td>
      <Table.Td ff="monospace" c="dimmed">{[b?.vpc, b?.vpc_nome].filter(Boolean).join(' ') || '—'}</Table.Td>
    </Table.Tr>
  )
}
